import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  IconButton
} from "@mui/material";
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { PlatformSummary } from "../types/platform";
import PlatformFormGeneral, {
  OTHER_TYPE_OPTION,
  PlatformFormValues,
  PRESET_PLATFORM_TYPES
} from "./PlatformFormGeneral";
import PlatformFormGeo from "./PlatformFormGeo";
import { normalizeTags } from "../utils/tags";
import { openExternalPath } from "../utils/externalLinks";

const DEFAULT_PLATFORM_COLOR = "#2563eb";
type PlatformDialogTab = "general" | "geo";

type PlatformDialogProps = {
  open: boolean;
  editingPlatform: PlatformSummary | null;
  platforms: PlatformSummary[];
  onClose: () => void;
  onSuccess: (result: { name: string; isEdit: boolean }) => void;
  authorizedFetch: (
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1]
  ) => Promise<Response>;
};

const defaultValues: PlatformFormValues = {
  name: "",
  type: "Plane",
  typeOption: "Plane",
  model: "",
  description: "",
  color: DEFAULT_PLATFORM_COLOR,
  tags: [],
  latitude: "",
  longitude: "",
  altitude: "",
  heading: "",
  locationSet: false
};

const PlatformDialog = ({
  open,
  editingPlatform,
  platforms,
  onClose,
  onSuccess,
  authorizedFetch
}: PlatformDialogProps) => {
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PlatformDialogTab>("general");

  const handleOpenConfigPlatformHelp = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    void openExternalPath("/help/user-guide/config-platform.html");
  }, []);

  useEffect(() => {
    if (!open) {
      setDialogError(null);
    }
  }, [open, editingPlatform]);

  useEffect(() => {
    if (open) {
      setActiveTab("general");
    }
  }, [open]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .required("Name is required")
          .test("no-spaces", "Platform name cannot contain spaces", function () {
            return typeof this.originalValue !== "string" || !/\s/.test(this.originalValue);
          })
          .test("unique-name", "A platform with this name already exists", function (value) {
            if (!value) {
              return true;
            }
            const normalizedName = value.trim().toLowerCase();
            const currentId = editingPlatform?.id ?? null;
            return !platforms.some(
              (platform) => platform.id !== currentId && platform.name.trim().toLowerCase() === normalizedName
            );
          }),
        type: Yup.string().transform((value) => value?.trim() ?? "").required("Type is required"),
        description: Yup.string().transform((value) => value?.trim() ?? ""),
        model: Yup.string().transform((value) => value?.trim() ?? ""),
        color: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .required("Color is required")
          .matches(/^#[0-9a-f]{6}$/i, "Color must be a valid hex code"),
        tags: Yup.array().of(Yup.string().transform((value) => value?.trim() ?? "")),
        altitude: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .test("altitude-range", "Altitude must be between -900 and 19000", (value) => {
            if (!value) return true;
            const num = Number(value);
            return Number.isFinite(num) && num >= -900 && num <= 19000;
          }),
        heading: Yup.string()
          .transform((value) => value?.trim() ?? "")
          .test("heading-range", "Heading must be between 0 and 360", (value) => {
            if (!value) return true;
            const num = Number(value);
            return Number.isFinite(num) && num >= 0 && num <= 360;
          })
      }),
    [editingPlatform?.id, platforms]
  );

  const editingPlatformColor = editingPlatform?.color?.trim() || DEFAULT_PLATFORM_COLOR;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <AirplanemodeActiveIcon sx={{ color: "text.secondary" }} />
          {editingPlatform ? "Edit platform" : "Add platform"}
        </Box>
      </DialogTitle>
      <Formik
        initialValues={
          editingPlatform
            ? {
              name: editingPlatform.name,
              type: editingPlatform.type,
              typeOption: PRESET_PLATFORM_TYPES.includes(editingPlatform.type.trim())
                ? editingPlatform.type
                : OTHER_TYPE_OPTION,
              model: editingPlatform.model?.trim() ?? "",
              description: editingPlatform.description ?? "",
              color: editingPlatformColor,
              tags: normalizeTags(editingPlatform.tags ?? []),
              latitude:
                editingPlatform.geoInfo?.latitude !== undefined && editingPlatform.geoInfo?.latitude !== null
                  ? String(editingPlatform.geoInfo.latitude)
                  : "",
              longitude:
                editingPlatform.geoInfo?.longitude !== undefined && editingPlatform.geoInfo?.longitude !== null
                  ? String(editingPlatform.geoInfo.longitude)
                  : "",
              altitude:
                editingPlatform.geoInfo?.altitude !== undefined && editingPlatform.geoInfo?.altitude !== null
                  ? String(editingPlatform.geoInfo.altitude)
                  : "",
              heading:
                editingPlatform.geoInfo?.heading !== undefined && editingPlatform.geoInfo?.heading !== null
                  ? String(editingPlatform.geoInfo.heading)
                  : "",
              locationSet: Boolean(editingPlatform.geoInfo)
            }
            : { ...defaultValues }
        }
        enableReinitialize
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setDialogError(null);
          try {
            const parseNumber = (value: string) => {
              const parsed = Number(value);
              return Number.isFinite(parsed) ? parsed : null;
            };

            const latitude = parseNumber(values.latitude);
            const longitude = parseNumber(values.longitude);
            const altitude = parseNumber(values.altitude);
            const heading = parseNumber(values.heading);
            const tags = normalizeTags(values.tags);

            const payload: Record<string, unknown> = {
              name: values.name.trim(),
              type: (values.typeOption === OTHER_TYPE_OPTION ? values.type : values.typeOption)?.trim() ?? "",
              model: values.model.trim(),
              description: values.description.trim(),
              color: (() => {
                const trimmed = values.color?.trim() ?? "";
                if (!trimmed) {
                  return DEFAULT_PLATFORM_COLOR;
                }
                return `#${trimmed.replace(/^#/, "").toLowerCase()}`;
              })(),
              tags
            };

            if (values.locationSet && latitude !== null && longitude !== null) {
              payload.geoInfo = {
                latitude,
                longitude,
                altitude: altitude ?? 0,
                heading: heading ?? 0
              };
            }
            const response = await authorizedFetch(
              editingPlatform ? `/api/platforms/${editingPlatform.id}` : "/api/platforms",
              {
                method: editingPlatform ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
              }
            );

            if (!response.ok) {
              const data = await response.json().catch(() => null);
              const message = data?.error ?? "Unable to save platform";
              throw new Error(message);
            }

            onSuccess({ name: values.name, isEdit: Boolean(editingPlatform) });
          } catch (err) {
            console.error(err);
            const message = err instanceof Error ? err.message : "Unable to save platform.";
            setDialogError(message);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, dirty, isValid }) => (
          <Form>
            <DialogContent dividers>
              <Stack spacing={2}>
                {dialogError ? <Alert severity="error">{dialogError}</Alert> : null}
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={activeTab}
                    onChange={(_event, value) => setActiveTab(value)}
                    aria-label="Platform form tabs"
                  >
                    <Tab label="General" value="general" />
                    <Tab label="Geo" value="geo" />
                  </Tabs>
                </Box>
                {activeTab === "general" ? <PlatformFormGeneral /> : <PlatformFormGeo />}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Tooltip title="Help">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleOpenConfigPlatformHelp}
                    aria-label="Open platform configuration help"
                    disabled={isSubmitting}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Box display="flex" gap={1}>
                <Button onClick={onClose} variant="outlined" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || !dirty || !isValid}>
                  {editingPlatform ? "Save" : "Add platform"}
                </Button>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default PlatformDialog;
